import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import {
  VRMLoaderPlugin,
  VRMUtils,
  type VRM,
  type VRMHumanBoneName,
} from '@pixiv/three-vrm';

export interface LoadedVrm {
  vrm: VRM;
  object: THREE.Object3D;
  /** Canonical humanoid key ('hips', 'leftarm', …) → raw VRM bone node. */
  targetMap: Map<string, THREE.Object3D>;
}

const loader = new GLTFLoader();
loader.register((parser) => new VRMLoaderPlugin(parser));

/**
 * VRM humanoid bone name → this app's canonical humanoid key (the same keys the
 * retarget alias cleaner produces for FBX bone names). VRM's `little` finger is
 * our `pinky`; the VRM1 thumb chain (metacarpal/proximal/distal) maps to joints
 * 1–3 like the other fingers' proximal/intermediate/distal.
 */
const VRM_TO_CANONICAL: Record<string, string> = {
  hips: 'hips',
  spine: 'spine',
  chest: 'spine1',
  upperChest: 'chest',
  neck: 'neck',
  head: 'head',
  leftShoulder: 'leftshoulder',
  leftUpperArm: 'leftarm',
  leftLowerArm: 'leftforearm',
  leftHand: 'lefthand',
  rightShoulder: 'rightshoulder',
  rightUpperArm: 'rightarm',
  rightLowerArm: 'rightforearm',
  rightHand: 'righthand',
  leftUpperLeg: 'leftupleg',
  leftLowerLeg: 'leftleg',
  leftFoot: 'leftfoot',
  leftToes: 'lefttoe',
  rightUpperLeg: 'rightupleg',
  rightLowerLeg: 'rightleg',
  rightFoot: 'rightfoot',
  rightToes: 'righttoe',
};

const FINGERS: Record<string, string> = {
  Thumb: 'thumb',
  Index: 'index',
  Middle: 'middle',
  Ring: 'ring',
  Little: 'pinky',
};
for (const side of ['left', 'right'] as const) {
  for (const [vrmFinger, finger] of Object.entries(FINGERS)) {
    const joints =
      vrmFinger === 'Thumb'
        ? ['Metacarpal', 'Proximal', 'Distal']
        : ['Proximal', 'Intermediate', 'Distal'];
    joints.forEach((joint, i) => {
      VRM_TO_CANONICAL[`${side}${vrmFinger}${joint}`] = `${side}hand${finger}${i + 1}`;
    });
  }
}

/**
 * Parse a `.vrm` avatar. We retarget onto the RAW humanoid bones (not three-vrm's
 * normalized proxies) so the existing world-space retargeter and the GLB export
 * baking see real scene-graph bones; `autoUpdateHumanBones` is disabled so
 * `vrm.update()` only runs spring bones/expressions and never stomps our pose.
 */
export async function parseVrm(buffer: ArrayBuffer): Promise<LoadedVrm> {
  const gltf = await loader.parseAsync(buffer, '');
  const vrm = gltf.userData.vrm as VRM | undefined;
  if (!vrm) throw new Error('No VRM avatar found in this file.');

  VRMUtils.removeUnnecessaryVertices(vrm.scene);
  VRMUtils.combineSkeletons(vrm.scene);
  // VRM 0.x models face the wrong way; this is a no-op for VRM 1.0.
  VRMUtils.rotateVRM0(vrm);
  vrm.humanoid.autoUpdateHumanBones = false;

  const targetMap = new Map<string, THREE.Object3D>();
  for (const [vrmName, canonical] of Object.entries(VRM_TO_CANONICAL)) {
    const node = vrm.humanoid.getRawBoneNode(vrmName as VRMHumanBoneName);
    if (node && !targetMap.has(canonical)) targetMap.set(canonical, node);
  }
  // Models without an upperChest: let their chest bone cover the deeper spine
  // canonicals so sources with Spine2/Chest still find a target.
  const chest = targetMap.get('spine1');
  if (chest) {
    if (!targetMap.has('chest')) targetMap.set('chest', chest);
    if (!targetMap.has('spine2')) targetMap.set('spine2', targetMap.get('chest') ?? chest);
  }

  vrm.scene.updateWorldMatrix(true, true);
  return { vrm, object: vrm.scene, targetMap };
}

/** Free a VRM's GPU resources (call when replacing/removing an avatar). */
export function disposeVrm(vrm: VRM): void {
  VRMUtils.deepDispose(vrm.scene);
}
